// Where to send a user after sign-in, when they arrived by following a link to a
// page that required authentication.
//
// PrivateRoute used to bounce an unauthenticated visitor to /sign-in and keep no
// record of what they had asked for, so every deep link resolved to the role's
// dashboard. For most pages that is a nuisance. For the OAuth consent screen it
// breaks the feature outright: the authorization server redirects a browser to
// /oauth/consent?request_id=..., and that request id is the only handle on the
// pending authorization. Lose it and the agent waits for a decision no one is
// ever shown.
//
// This is deliberately not the existing SSO `sourcePath` (see storeSource): that
// key is cleared by the SSO landing route whenever it loads without a sourcePath
// parameter, so a value parked there would not survive the trip. A separate key
// survives every route to a signed-in state - password, MFA and SSO alike -
// because localStorage outlives the SSO round trip through the identity
// provider.

const KEY = 'vivalnk_post_login_path';

// A stored target is only ever a path within this app. Anything else - an
// absolute URL, a protocol-relative "//host" that the browser would treat as
// another origin, a stray backslash that some parsers fold into a slash - is
// discarded rather than followed, because this value reaches history.push and an
// attacker who can seed localStorage should not thereby choose where a freshly
// authenticated user lands.
function isSafeInternalPath(path: string): boolean {
  if (!path.startsWith('/')) {
    return false;
  }
  if (path.startsWith('//') || path.startsWith('/\\')) {
    return false;
  }
  return true;
}

export function storePostLoginPath(path: string): void {
  if (!isSafeInternalPath(path)) {
    return;
  }
  try {
    localStorage.setItem(KEY, path);
  } catch {
    // A full or blocked localStorage costs the redirect, not the login.
  }
}

export function removePostLoginPath(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

// A plain read, for a component that needs the value while rendering and so
// must not clear it there. Pair it with removePostLoginPath in an effect.
export function getPostLoginPath(): string {
  let stored = '';
  try {
    stored = localStorage.getItem(KEY) || '';
  } catch {
    return '';
  }
  return isSafeInternalPath(stored) ? stored : '';
}

// Reads the target and clears it in the same breath: it describes one pending
// navigation, and leaving it behind would hijack the next ordinary sign-in.
export function consumePostLoginPath(): string {
  let stored = '';
  try {
    stored = localStorage.getItem(KEY) || '';
  } catch {
    return '';
  }
  removePostLoginPath();
  return isSafeInternalPath(stored) ? stored : '';
}
